import { Component, OnInit, Output, EventEmitter, Input, HostBinding } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { transition, style, animate, trigger } from '@angular/animations';
import { Store } from '@ngrx/store';
import { seShowPdfOnModal } from '@se/platform';
import { ButtonClick, Logger, NotificationType, NotificationService, ModalDialogNotificationService } from '@se/se.ui.html5';
import EstensaModel, { ImagingOpenParams } from 'estensa-module';
import { Subscription, firstValueFrom } from 'rxjs';
import { IRadioEprEventEx } from '../se-side-patient-history.component';
import { RootState } from '../../../../root-store/root.state';
import { BrokerCheckService } from '../../../../services/angularjs/broker-check.service';
import { RadioSettingsService } from '../../../../services/settings/radio-settings.service';
import { ImagingService } from '../../../../services/imaging/imaging.service';
import { EstensaHealthPlatformService } from '../../../../services/angularjs/health-platform.service';
import { EstensaWaitingService } from '../../../../services/waiting.service';
import { Actions, ofType } from "@ngrx/effects";
import { seTimelineComponentSetSelectedItem } from "../../../../se-timeline/redux/se-timeline.actions";

@Component({
    selector: 'se-side-patient-history-item',
    templateUrl: './se-side-patient-history-item.component.html',
    styleUrls: ['./se-side-patient-history-item.component.scss'],
    animations: [
        trigger('expansionAnimation', [
            transition(':enter', [style({ opacity: 0 }), animate('400ms ease-in', style({ opacity: 1 }))]),
            transition(':leave', [animate('400ms ease-out', style({ opacity: 0 }))]),
        ]),
    ],
})
export class SeSidePatientHistoryItemComponent implements OnInit {

    @Input() item : IRadioEprEventEx;
    @Output() historySelectionChanged = new EventEmitter<IRadioEprEventEx>();
    @HostBinding('style') style = {};

    expanded: boolean = false;
    imagesLoades: boolean = false;
    // By default chiediamo se si vuole scaricare il viewer dalle immagini precedenti
    reportImageActionType : EstensaModel.ImageActionType = EstensaModel.ImageActionType.Ask;
    timelineItemSelectionSubscription : Subscription;

    constructor(
        private brokerCheckService: BrokerCheckService,
        private healthPlatformService: EstensaHealthPlatformService,
        private imagingService: ImagingService,
        private logger: Logger,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private notificationService: NotificationService,
        private radioSettingsService: RadioSettingsService,
        private store: Store<RootState>,
        private translate: TranslateService,
        private actions : Actions,
        private waitingService: EstensaWaitingService,
    ) {}

    ngOnInit () {
        this.listenForStoreEvents();
        this.radioSettingsService.radioSettingsAsync.subscribe(
            (settings) => (this.reportImageActionType = settings.reportSettings.reportImageActionType),
        );
    }

    onCardSelected() {
        if (!this.item.selected) {
            this.item.selected = true;
            this.historySelectionChanged.emit(this.item);
        }
    }

    getReportDateString() {
        return this.translate.instant('ReportDate') + ': ' + this.item.reportDateString;
    }

    openPdf(event) {
        event.stopPropagation();

        this.onCardSelected();

        this.store.dispatch(seShowPdfOnModal({ documentId: this.item.reportKey /* SOP Instace Uid */ }));
    }

    async openImagesByEprEvent(event) {
        event.stopPropagation();

        this.onCardSelected();

        let discardPreviousImages = true; // By default scarichiamo le immagini (no append)

        switch (this.reportImageActionType) {
            case EstensaModel.ImageActionType.Ask:
                if (this.imagingService.areImagesOpen()) {
                    let buttonClick: ButtonClick = await firstValueFrom(
                        this.modalDialogNotificationService.notify({
                            buttonYes: true,
                            buttonNo: true,
                            buttonCancel: false,
                            buttonOk: false,
                            title: this.translate.instant('Warning'),
                            message: this.translate.instant('DiscardQuestion_Text'),
                            type: NotificationType.information,
                        }),
                    );

                    discardPreviousImages = buttonClick == ButtonClick.yes;
                }
                break;
            case EstensaModel.ImageActionType.Append:
                discardPreviousImages = false;
                break;
            case EstensaModel.ImageActionType.Discard:
                break;
        }

        const openParam: ImagingOpenParams = {
            discardPreviousImages: discardPreviousImages,
            appendToPreviousImages: !discardPreviousImages,
            useAlternativeUrl: false,
        };

        this.brokerCheckService.openImagesByEprEvent([this.item], null, openParam);
    }

    async checkViewerAppendCapability() {
        let type = await this.imagingService.getViewerType();
        switch (type) {
            // Qui tutti i visualizzatori che supportano l'append di immagini
            case 'eUnity':
            case 'EstensaReview':
            case 'EstensaReviewVNext':
            case 'SeWebViewer':
                return;
            default:
                this.logger.warn(
                    `Viewer ${type} is not able to handle multiple studies images concatenation, but reportImageActionType setting value is 'Ask' or 'Append'`,
                );
        }
    }

    openParameters(event) {
        event.stopPropagation();

        this.onCardSelected();

        const healthPatient: EstensaModel.HealthPlatformPatient = {
            id: this.item.patient.id,
            taxCode: this.item.patient.taxCode,
        };

        this.waitingService.isWaiting = true;

        this.healthPlatformService
            .openPageAsync(EstensaModel.HealthPlatformOperation.MEASURES, healthPatient)
            .catch((e) => {
                this.notificationService.httpEstensaFaultError(
                    e,
                    this.translate.instant('Error'),
                    this.translate.instant('Exception_HealthPlatformFault'),
                );
            })
            .finally(() => {
                this.waitingService.isWaiting = false;
            });
    }

    areDataInExpanderPresent(item): boolean {
        return !!item.businessUnit || !!item.archiveNumber || !!item.admissionNumber || !!item.externalAdmissionNumber;
    }

    listenForStoreEvents () {
        this.timelineItemSelectionSubscription = this.actions.pipe(ofType(seTimelineComponentSetSelectedItem)).subscribe((action) => {
            // TODO_DGS implementare selezione della card che corrisponde all'id dell'elemento selezionato in timeline
            // NB: forse il formType non è necessario
            console.log(action, 'formType + selectedItem')
        })
    }
}
